// frontend/src/store/project-opex-slice.tsx
// Slices own their state, dirty tracking, validation, and payload building
import { useCallback, useEffect, useMemo, useState } from "react";
import i18n from "@/i18n";
import api from "@/utils/axios";
import { fromDecimalToPercent, fromPercentToDecimal, percentToDecimalString, decimalToPercentString } from "@/utils/percent";
import { validateOpexRows, OpexValidationIssue } from "@/utils/validate-opex";

export type Primitive = string | number | boolean | null | undefined;
export type ProjectData = Record<string, Primitive>;

export type ProjectOpexUnit =
  | "EUR_PER_KWP"
  | "EUR_CT_PER_KWH"
  | "PERCENT_OF_SALES"
  | "EUR"
  | "PERCENT_OF_TOTAL_INVESTMENT";


type Decimalish = number | string; // payload can be a number or a precise decimal string

export interface ProjectOpexItemPayload {
  id?: number;
  project_id: number;
  name?: string | null;
  value: Decimalish;             // ← allow string or number // decimal or absolute, depending on unit
  unit: ProjectOpexUnit;
  start_year?: number | null;   // allow SOP
  end_year?: number | null;     // allow EOP
  annual_increase?: Decimalish;     // ← allow string or number // decimal (0.02 = 2%)
  position?: number;
}

export interface ProjectOpexRowUI {
  id: number;                   // 0 => not yet created
  project_id: number;
  name: string;                 // empty string => "(unnamed)" placeholder in UI
  valueStr: string;             // text; percent in UI for percent units
  unit: ProjectOpexUnit;
  startStr: string;             // e.g., "1"
  endStr: string;               // "" or "21"
  annualPercentStr: string;     // e.g., "2" for 2%
  position: number;
}

/* ---------- helpers ---------- */

const isPercentUnit = (u: ProjectOpexUnit) =>
  u === "PERCENT_OF_SALES" || u === "PERCENT_OF_TOTAL_INVESTMENT";

const parseNum = (s: string): number | null => {
  const t = (s ?? "").trim();
  if (!t) return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const toUI = (p: ProjectOpexItemPayload): ProjectOpexRowUI => {
  const valueStr = isPercentUnit(p.unit)
    ? (
        p.value == null
          ? ""
          : (typeof p.value === "string"
              ? decimalToPercentString(p.value)                // exact string math (no float)
              : String(fromDecimalToPercent(Number(p.value)))  // fallback if BE sent a number
            )
      )
    : (p.value == null ? "" : String(p.value));

  return {
    id: p.id ?? 0,
    project_id: p.project_id,
    name: p.name ?? "",
    valueStr,
    unit: p.unit,
    startStr: p.start_year == null ? "" : String(p.start_year),
    endStr: p.end_year == null ? "" : String(p.end_year),
    annualPercentStr:
      p.annual_increase == null
        ? ""
        : (typeof p.annual_increase === "string"
            ? decimalToPercentString(p.annual_increase)           // exact string math
            : String(fromDecimalToPercent(Number(p.annual_increase)))),
    position: p.position ?? 0,
  };
};


const toPayload = (projectId: number, r: ProjectOpexRowUI): ProjectOpexItemPayload => {
  // Parse numeric inputs
  const rawVal = parseNum(r.valueStr);
  const isPct = isPercentUnit(r.unit);

  // Value: percent units expect decimal on BE (e.g., 2% => 0.02)
  const value = isPct
    ? ((rawVal ?? 0) / 100)
    : (rawVal ?? 0); // absolute numbers; blank => 0

  // Years: blank => null (SOP/EOP), integers otherwise
  const s = parseNum(r.startStr);
  const e = parseNum(r.endStr);
  const start_year = Number.isFinite(s as number) ? Math.trunc(s as number) : null;
  const end_year   = Number.isFinite(e as number) ? Math.trunc(e as number) : null;

  // Annual increase: percent string -> decimal number (blank => 0)
  const ap = parseNum(r.annualPercentStr);
  const annual_increase = ap == null ? 0 : (ap / 100);

  // Build payload with ONLY numbers or nulls for numeric fields
  return {
    id: r.id,
    project_id: projectId,
    name: r.name?.trim() ? r.name.trim() : null,
    unit: r.unit,
    value,                // number
    start_year,           // number | null
    end_year,             // number | null
    annual_increase,      // number
    position: r.position ?? 0,
  };
};


/* ---------- slice hook ---------- */

export function useProjectOpexSlice(project: ProjectData | null) {
  const [opexRows, setOpexRows] = useState<ProjectOpexRowUI[] | null>(null);
  const [opexOriginal, setOpexOriginal] = useState<ProjectOpexRowUI[] | null>(null);
  const [opexSaving, setOpexSaving] = useState<boolean>(false);
  const [opexError, setOpexError] = useState<string | null>(null);

  const projectId = Number((project as any)?.project_id || 0);

  const loadOpexItems = useCallback(async () => {
    if (!projectId) {
      setOpexRows([]);
      setOpexOriginal([]);
      return;
    }
    try {
      const { data } = await api.get(`/api/projects/${projectId}/opex-items`);
      const sorted = (data as ProjectOpexItemPayload[])
        .slice()
        .sort(
          (a, b) =>
            (a.position ?? 0) - (b.position ?? 0) ||
            (a.id ?? 0) - (b.id ?? 0)
        );
      const ui = sorted.map(toUI);
      setOpexRows(ui);
      setOpexOriginal(ui);
      setOpexError(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.statusText ||
        err?.message ||
        i18n.t("project.opex.load_failed");
      setOpexError(msg);
      setOpexRows([]);
      setOpexOriginal([]);
    }
  }, [projectId]);

  // auto-load when project changes
  useEffect(() => {
    if (projectId) {
      loadOpexItems();
    } else {
      setOpexRows([]);
      setOpexOriginal([]);
    }
  }, [projectId, loadOpexItems]);

  const isOpexDirty = useMemo(() => {
    if (!opexRows || !opexOriginal) return false;
    return JSON.stringify(opexRows) !== JSON.stringify(opexOriginal);
  }, [opexRows, opexOriginal]);


  const validate = useCallback((): OpexValidationIssue[] => {
    return validateOpexRows(opexRows ?? []);
  }, [opexRows]);


  const saveOpexItems = useCallback(async () => {
    if (!projectId || !opexRows) return;
    setOpexSaving(true);
    try {
      const origById = new Map((opexOriginal ?? []).map((r) => [r.id, r]));
      const toCreate = opexRows.filter((r) => r.id === 0);
      const toUpdate = opexRows.filter((r) => {
        if (r.id === 0) return false;
        const o = origById.get(r.id);
        return JSON.stringify(r) !== JSON.stringify(o);
      });
      const currentIds = new Set(
        opexRows.filter((r) => r.id !== 0).map((r) => r.id)
      );
      const toDelete = (opexOriginal ?? []).filter(
        (r) => r.id !== 0 && !currentIds.has(r.id)
      );

      for (const c of toCreate) {
        const payload = toPayload(projectId, c);
        const { data } = await api.post(`/api/projects/${projectId}/opex-items`, payload );

        // Accept both legacy raw item and new envelope with `item`
        const returned = (data && typeof data === "object" && "item" in data)
          ? (data.item as ProjectOpexItemPayload)
          : (data as ProjectOpexItemPayload);

        const createdUI = toUI(returned);
      }


      for (const u of toUpdate) {
        const payload = toPayload(projectId, u);
        await api.put(`/api/projects/${projectId}/opex-items/${u.id}`, payload);
      }

      for (const d of toDelete) {
        await api.delete(`/api/projects/${projectId}/opex-items/${d.id}`);
      }

      await loadOpexItems();

      setOpexError(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        i18n.t("project.opex.save_failed");
      setOpexError(msg);
    } finally {
      setOpexSaving(false);
    }
  }, [projectId, opexRows, opexOriginal, loadOpexItems]);

  const resetOpexItems = useCallback(() => {
    setOpexRows(opexOriginal ? JSON.parse(JSON.stringify(opexOriginal)) : []);
    setOpexError(null);
  }, [opexOriginal]);

  const commitOpex = useCallback(() => {
    setOpexOriginal(opexRows ? JSON.parse(JSON.stringify(opexRows)) : []);
  }, [opexRows]);

  return {
    opexRows,
    setOpexRows,
    loadOpexItems,
    saveOpexItems,
    resetOpexItems,
    commitOpex,
    opexSaving,
    opexError,
    isOpexDirty,
    validate,
  };
}